import { Check, Copy, Info } from "lucide-react";
import { useState } from "react";
import { ApiRequestError } from "../api/client";
import { common } from "../i18n/common";
import { useT } from "../i18n/core";
import { osLabel } from "../lib/status";

export function PageHead({ title, lede, eyebrow, actions }: { title: React.ReactNode; lede?: React.ReactNode; eyebrow?: string; actions?: React.ReactNode }) {
  return (
    <header className="page-head">
      <div>
        {eyebrow && <div className="eyebrow">{eyebrow}</div>}
        <h1>{title}</h1>
        {lede && <p className="lede">{lede}</p>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </header>
  );
}

export function Panel({ title, sub, actions, children, foot, bodyless }: {
  title?: React.ReactNode; sub?: React.ReactNode; actions?: React.ReactNode; children: React.ReactNode; foot?: React.ReactNode; bodyless?: boolean;
}) {
  return (
    <section className="panel">
      {(title || actions) && (
        <div className="panel-head">
          <div>
            {title && <h2>{title}</h2>}
            {sub && <div className="sub">{sub}</div>}
          </div>
          {actions}
        </div>
      )}
      {bodyless ? children : <div className="panel-body">{children}</div>}
      {foot && <div className="panel-foot">{foot}</div>}
    </section>
  );
}

export function Skeleton({ h = 16, w = "100%" }: { h?: number; w?: number | string }) {
  return <div className="skeleton" style={{ height: h, width: w }} aria-hidden="true" />;
}

export function LoadingRows({ rows = 6 }: { rows?: number }) {
  const t = useT(common);
  return (
    <div style={{ display: "grid", gap: 12, padding: 18 }} aria-busy="true" aria-label={t.loading}>
      {Array.from({ length: rows }, (_, i) => <Skeleton key={i} h={18} w={`${90 - (i % 3) * 12}%`} />)}
    </div>
  );
}

export function ErrorState({ error }: { error: unknown }) {
  const t = useT(common);
  const msg = error instanceof ApiRequestError ? error.message : t.loadError;
  return (
    <div className="callout crit" role="alert">
      <Info aria-hidden="true" />
      <span>{msg}</span>
    </div>
  );
}

export function CodeBlock({ code, label }: { code: string; label: string }) {
  const t = useT(common);
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  }
  return (
    <div className="codeblock">
      <pre aria-label={label}>{code}</pre>
      <button type="button" onClick={copy} aria-label={copied ? t.copied : t.copyLabel(label)} title={copied ? t.copied : t.copy}>
        {copied ? <Check /> : <Copy />}
      </button>
    </div>
  );
}

export function OsTag({ os }: { os: "windows" | "macos" | "linux" }) {
  return <span className="tag">{osLabel[os]}</span>;
}
